<template>
  <div
    ref="tableMessege"
    :class="[$style.searchResultContainer]"
  >
    <div
      v-if="grouppedColumns.length === 0 && !isResultEmpty"
      :class="$style.groupHint"
    >
      {{ $t('table.moveColumnForGrouping') }}
    </div>
    <draggable
      :class="$style.draggableInput"
      :list="grouppedColumns"
      :group="{
        name: 'columns',
      }"
      :move="onMove"
      @change="move"
    >
      <div
        v-for="(element, idx) in grouppedColumns"
        :key="element.name"
      >
        <div :class="$style.grouppedItem">
          <div :class="$style.grouppedColumnName">
            {{ element.name }}
          </div>
          <button
            type="button"
            :class="$style.btnClose"
            aria-label="Close modal"
            @click="removeAt(idx)"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
    </draggable>
    <div
      v-if="isResultEmpty"
      :class="$style.emptyResult"
    >
      {{ emptyResultString }}
    </div>
    <table
      v-else
      id="tableHead"
      :class="$style.table"
    >
      <thead :class="$style.head">
        <draggable
          :class="$style.draggableColumns"
          :list="reorderedColumns"
          :group="{
            name: 'columns',
            pull: 'clone',
            put: false,
          }"
          :sort="false"
          tag="tr"
          :move="onMove"
          @change="move"
        >
          <td
            v-for="column in reorderedColumns"
            :key="column.id"
            :class="[$style.headTd, $style.td]"
            :style="{ width: column.width }"
            @dblclick="$emit('changeSort', column)"
          >
            <div :class="{ [$style.container]: isWidthTable }">
              <div :class="$style.tdInline">
                {{ column.name }}
              </div>
              <div :class="$style.tdInline">
                <img
                  src="@/assets/images/icons/table/sort-arrow.svg"
                  :class="[
                    $style.tdInline,
                    $style.arrow,
                    {
                      [$style.asc]: column.sort == 2,
                      [$style.hidden]: column.sort == 0,
                    },
                  ]"
                  @click="$emit('changeSort', column)"
                >
                <img
                  :id="'filterIcon' + column.field"
                  src="@/assets/images/icons/navigation/filter.svg"
                  :class="[$style.tdInline]"
                  @click="$emit('filterClick', column)"
                >
                <div class="dropdown">
                  <treeselect
                    :id="'columnFilter' + column.field"
                    v-model="selected[column.field]"
                    :multiple="true"
                    :options="column.filter"
                    :always-open="column.showFilter"
                    :normalizer="normalizer"
                    @input="filterGroupedData"
                  />
                </div>
              </div>
            </div>
          </td>
        </draggable>
      </thead>
    </table>
    <table
      v-if="grouppedColumns.length === 0"
      id="tableBody"
    >
      <tbody v-if="tableData.length">
        <tr
          v-for="(item, index) in sortedData"
          :key="item.id.toString() + index.toString()"
          :tabindex="index + 1"
          :class="[
            { [$style.active]: selectedRow && item.id === selectedRow.id },
            getRowParameterClasses(item.id),
            $style.tr,
          ]"
          @click="selectRow(item)"
          @focus="selectRow(item)"
          @keydown.arrow-up.prevent="focusRow(index - 1)"
          @keydown.arrow-down.prevent="focusRow(index + 1)"
        >
          <td
            v-for="(column, index) in reorderedColumns"
            :key="item.id + column.field"
            :style="{ width: column.width }"
            :class="[
              {
                [$style.firstColumn]: index === 0,
                [$style.lastColumn]: index === columns.length - 1,
              },
              $style.td,
            ]"
          >
            <div v-if="column.field === 'date'">
              <p
                v-if="item.isNew"
                :class="$style.new"
              >
                NEW
              </p>
              {{ item[column.field] }}
            </div>
            <div v-else-if="column.field === 'messenger'">
              <img
                :src="item.messengerIcon"
                :class="$style.messengerIcon"
              >
              <span :class="$style.messengerName">{{ item.messenger }}</span>
            </div>
            <div v-else-if="column.field === 'interlocutor'">
              <span :class="$style.dangerous">
                <dangerous :value="item.statusDangerous" />
              </span>
              <span v-if="item.customNickname">{{ item.customNickname }} ({{ item.interlocutor }})</span>
              <span v-else>
                {{ item.interlocutor }}
              </span>
            </div>
            <div v-else-if="column.field === 'direction'">
              <img
                :src="item.directionIcon"
                :class="$style.directionIcon"
              >
              <span :class="$style.directionName">{{ item.direction }}</span>
            </div>
            <div v-else-if="column.field === 'text'">
              <column-text
                :row="item"
                :record-id="item.id"
              />
            </div>
            <div v-else>
              {{ item[column.field] }}
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td
            :colspan="columns.length"
            :class="$style.noData"
          >
            {{ $t('table.noData') }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else>
      <grouped-table-view
        :data="sortedGroupData"
        :columns="reorderedColumns"
        :depth="0"
        :padding="0"
        :selected-row="selectedRowId"
        @changeSelectedRow="selectRow"
      />
    </div>
  </div>
</template>

<script>
  import Dangerous from '@/components/common/dangerous/index.vue';
  import draggable from 'vuedraggable';
  import Treeselect from '@riophae/vue-treeselect';
  import GroupedTableView from '@/components/common/table/groupedTableView.vue';
  import ColumnText from '@/components/pages/computers/log/messengers/components-default/column-text.vue';

  export default {
    components: {
      draggable,
      Dangerous,
      GroupedTableView,
      Treeselect,
      ColumnText,
    },
    props: ['tableData', 'searchString', 'emptyResultString', 'columns'],
    data: () => ({
      notGroupedColumns: [],
      selected: [],
      grouppedColumns: [],
      groupResult: [],
      isResultEmpty: false,
      selectedRow: null,
      tableWidth: 0,
    }),
    computed: {
      isWidthTable() {
        return this.tableWidth < 1000;
      },
      dataParameters() {
        return this.tableData.reduce((acc, item) => {
          acc[item.id] = {
            color: {
              1: 'yellow',
              2: 'red',
            }[item.statusDangerous],
          };

          return acc;
        }, {});
      },
      filteredData() {
        let result = this.tableData.slice(0);
        Object.entries(this.selected).forEach(([key, value]) => {
          if (value.length > 0) {
            result = result.filter((item) => value.indexOf(item[key]) > -1);
          }
        });
        return result;
      },
      sortedData() {
        const calc = this.filteredData.slice(0).sort(this.compare);
        this.$store.commit('logs/printData/setData', calc);
        return calc;
      },
      sortedGroupData() {
        Object.keys(this.groupResult).forEach((key) => {
          this.sortNodes(this.groupResult[key]);
        });
        return this.groupResult;
      },
      selectedRowId() {
        if (this.selectedRow) return this.selectedRow.id;
        return null;
      },
      reorderedColumns() {
        const result = [];
        this.grouppedColumns.forEach((col) => result.push(col));
        if (result.length === 0) return this.columns;
        if (result.length < this.columns.length) {
          this.columns.forEach((column) => {
            const found = this.grouppedColumns.find((item) => item.field === column.field);
            if (!found) result.push(column);
          });
        }
        return result;
      },
    },
    watch: {
      grouppedColumns: {
        handler(newVal) {
          const currentView = this.$route.query.view || 'grid';

          const newView = newVal.length !== 0 ? 'groupped' : 'grid';

          if (currentView !== newView) {
            this.$router.push({
              query: {
                ...this.$route.query,
                view: newView,
              },
            });
          }
        },
        deep: true,
      },
      tableData(newArray, oldArray) {
        if (oldArray !== newArray) {
          this.selectedRow = null;
          if (newArray.length > 0) this.isResultEmpty = false;
          else this.isResultEmpty = true;

          this.notGroupedColumns = this.columns.slice();

          if (this.grouppedColumns.length > 0) this.group();
        }
      },
    },
    mounted() {
      this.handleResize();
      const block = this.$refs.tableMessege;
      const resizeObserver = new ResizeObserver(this.handleResize);
      resizeObserver.observe(block);
    },
    beforeDestroy() {
      const block = this.$refs.tableMessege;
      const resizeObserver = new ResizeObserver(this.handleResize);
      resizeObserver.unobserve(block);
    },
    methods: {
      handleResize(entries) {
        if (entries && entries.length > 0) {
          const entry = entries[0];
          const { width } = entry.contentRect;
          this.tableWidth = width;
        }
      },
      selectRow(row) {
        this.selectedRow = row;
        this.$emit('row-click', row.id);
      },
      focusRow(idx) {
        const elems = document.getElementsByTagName('tr');

        for (let i = elems.length; i--; ) {
          const tidx = parseInt(elems[i].getAttribute('tabindex'), 10);
          if (tidx === idx + 1) {
            elems[i].focus();
          }
        }
      },

      getRowParameterClasses(rowId) {
        const row = this.dataParameters[rowId];

        if (!row) {
          return [];
        }
        const classes = [];

        // eslint-disable-next-line default-case
        switch (row.color) {
          case 'yellow':
            classes.push(this.$style.colorYellow);
            break;
          case 'red':
            classes.push(this.$style.colorRed);
            break;
        }

        return classes;
      },

      filterGroupedData() {
        if (this.grouppedColumns.length > 0) {
          this.group();
        }
      },
      normalizer(node) {
        return {
          id: node,
          label: node,
        };
      },
      sortNodes(data) {
        if (data.array && Array.isArray(data.array)) {
          data.array.sort(this.compare);
        } else {
          Object.keys(data).forEach((key) => {
            this.sortNodes(data[key]);
          });
        }
      },
      compare(a, b) {
        const index = this.columns.findIndex((x) => x.sort > 0);
        const key = this.columns[index].field;

        a = a[key];
        b = b[key];

        if (typeof a === 'string' && typeof b === 'string') {
          a = a.toLowerCase();
          b = b.toLowerCase();
        }

        if (this.columns[index].sort === 1) {
          if (a > b) {
            return -1;
          }

          if (a < b) {
            return 1;
          }
          return 0;
        }

        if (this.columns[index].sort === 2) {
          if (a < b) {
            return -1;
          }

          if (a > b) {
            return 1;
          }

          return 0;
        }
        return 0;
      },
      move(evt) {
        let dragged = null;
        if (evt.added) dragged = evt.added.element.field;
        if (evt.moved) dragged = evt.moved.element.field;

        this.group();

        const indexOfObject = this.notGroupedColumns.findIndex((object) => object.field === dragged);

        if (indexOfObject > -1) this.notGroupedColumns.splice(indexOfObject, 1);
      },
      onMove(evt) {
        if (evt.from.nodeName === 'TR') {
          const exists = this.grouppedColumns.find((col) => col.field === evt.draggedContext.element.field);
          if (exists) return false;
          return true;
        }
        return true;
      },
      removeAt(idx) {
        const column = this.grouppedColumns[idx];
        this.grouppedColumns.splice(idx, 1);
        this.notGroupedColumns.push(column);
        if (this.grouppedColumns.length > 0) this.group();
      },
      groupRecursive(grouppedResult, columns) {
        if (columns.length > 0) {
          // eslint-disable-next-line
          for (const key of Object.keys(grouppedResult)) {
            if (grouppedResult[key].array && Array.isArray(grouppedResult[key].array)) {
              const groupObj = {};

              const groupField = columns[0].field;

              for (let i = 0; i < grouppedResult[key].array.length; i++) {
                const fieldValue = grouppedResult[key].array[i][groupField];

                if (!groupObj[fieldValue]) {
                  groupObj[fieldValue] = {
                    expanded: true,
                    array: [grouppedResult[key].array[i]],
                  };
                } else {
                  groupObj[fieldValue].array.push(grouppedResult[key].array[i]);
                }
              }
              groupObj.expanded = grouppedResult[key].expanded;
              grouppedResult[key] = groupObj;
            }

            this.groupRecursive(grouppedResult[key], columns.slice(1));
          }
        }
      },
      group() {
        const data = this.sortedData;
        const groupField = this.grouppedColumns[0].field;

        const grouppedResult = {};

        data.forEach((item) => {
          if (!grouppedResult[item[groupField]]) {
            grouppedResult[item[groupField]] = {
              expanded: true,
              array: [item],
            };
          } else {
            grouppedResult[item[groupField]].array.push(item);
          }
        });

        this.groupRecursive(grouppedResult, this.grouppedColumns.slice(1));
        this.groupResult = grouppedResult;
      },
    },
  };
</script>

<style lang="scss" module>
  .dangerous {
    display: inline-block;
    margin-right: 8px;
  }
  .messengerIcon {
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
    width: 20px;
    height: 20px;
  }
  .messengerName {
    vertical-align: middle;
  }
  .directionIcon {
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
  }
  .directionName {
    vertical-align: middle;
  }
  .new {
    display: inline-block;
    background: $light-blue;
    color: $blue;
    font-size: 7px;
    padding: 3px;
    margin-right: 8px;
    border-radius: 1px;
  }
  .searchResultContainer {
    display: flex;
    border: 1px solid #c4c4c4;
    height: 60%;
    flex-direction: column;
  }
  .emptyResult {
    margin: 30px;
    display: flex;
    justify-content: center;
  }

  .table {
    word-break: break-word;
    overflow: auto;
    padding: 10px;
    border-collapse: collapse;
    background: white;
    font-weight: 500;
    cursor: pointer;
  }
  .container {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .headTd {
    background: #dbf1ff;
  }
  .noData {
    padding: 6px;
    font-size: 12px;
  }
  .td {
    min-height: 30px;
    padding: 6px;
    letter-spacing: -0.08px;
    font-size: 13px;
    border: 1px solid #e3e3e3;
    max-width: 500px;
    word-break: break-word;
  }
  .firstColumn {
    padding-left: 20px;
  }
  .lastColumn {
    padding-right: 20px;
  }
  .draggableInput {
    width: 100%;
    display: flex;
    background: white;
    padding: 10px;
  }

  .grouppedItem {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 30px;
    margin: 5px;
    border: 1px solid $borderColor;
    font-size: 13px;
    cursor: grab;
    background: #aedefd26;
  }
  .btnClose {
    border: none;
    font-size: 18px;
    cursor: pointer;
    font-weight: bold;
    background: transparent;
    margin: 10px;
  }
  .grouppedColumnName {
    margin-left: 10%;
  }
  .groupHint {
    padding: 10px;
    font-size: 13px;
    color: #9a99a2;
    background: white;
    line-height: 30px;
  }
  .tr {
    cursor: pointer;
    &:hover {
      background: $light-gray;
    }
    &.active {
      background: $light-gray;
    }
  }
  .colorYellow {
    background-color: $light-yellow;

    &:hover {
      background: $yellow !important;
    }

    &.active {
      background: $yellow !important;
    }
  }
  .colorRed {
    background-color: $light-red;

    &:hover {
      background: $red !important;
    }

    &.active {
      background: $red !important;
    }
  }
  .tdInline {
    display: inline-flex;
  }
  .arrow {
    margin-top: 7px;
    margin: 0px 5px;
    &.asc {
      transform: rotate(180deg);
    }
    &.hidden {
      visibility: hidden;
    }
  }
  .icon {
    display: inline-flex;
    margin-right: 10px;
    width: 15px;
  }
</style>

<style>
  .sortable-ghost {
    border: 1px dashed grey;
    font-size: 0;
    overflow: hidden;
    margin: 5px;
  }
  .vue-treeselect {
    position: relative;
    text-align: left;
  }
  .vue-treeselect__menu {
    position: absolute;
    width: 300px;
    z-index: 1000;
    background-color: #fff;
    border: 1px solid #ccc;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  .dropdown .vue-treeselect__control {
    display: none !important;
  }
  #columnFilterusername .vue-treeselect__menu {
    left: -206px;
  }
</style>
